import { GoogleGenAI } from "@google/genai";

let aiClient: GoogleGenAI | null = null;

function getAI(): GoogleGenAI {
  if (!aiClient) {
    const apiKey = process.env.GEMINI_API_KEY || "";
    aiClient = new GoogleGenAI({ apiKey: apiKey || "dummy-key" });
  }
  return aiClient;
}

async function retryWithBackoff<T>(fn: () => Promise<T>, retries = 3, delay = 1000): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    // Check if error is rate limit (often contains "429")
    const errMessage = error instanceof Error ? error.message : String(error);
    if (errMessage.includes('429') || errMessage.includes('quota') || errMessage.includes('Rate limit')) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(fn, retries - 1, delay * 2);
    }
    throw error;
  }
}

export async function analyzeAccountingImage(base64Image: string, mimeType: string) {
  return retryWithBackoff(async () => {
    try {
      const ai = getAI();
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [
          {
            parts: [
              {
                text: "Analyze this accounting system screenshot. Identify the key features, layout, color scheme, and data fields. Provide a structured plan to recreate this system, including the necessary database entities and UI components. Return the analysis in a clear, structured format.",
              },
              {
                inlineData: {
                  data: base64Image.split(',')[1] || base64Image,
                  mimeType: mimeType,
                },
              },
            ],
          },
        ],
      });

      return response.text;
    } catch (error) {
      console.error("Error analyzing image:", error);
      throw error;
    }
  });
}

export async function extractExpenseDetails(base64Image: string, mimeType: string): Promise<any> {
  return retryWithBackoff(async () => {
    try {
      const ai = getAI();
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [
          {
            parts: [
              {
                text: `Extract the following details from this expense bill/receipt image:
                - Supplier/Vendor Name
                - Invoice/Receipt Date
                - Total Amount
                - Currency
                - Line Items (Description, Quantity, Unit Price, Total)
                
                Return the data in valid JSON format.`,
              },
              {
                inlineData: {
                  data: base64Image.split(',')[1] || base64Image,
                  mimeType: mimeType,
                },
              },
            ],
          },
        ],
        config: {
          responseMimeType: "application/json",
        }
      });

      return JSON.parse(response.text || "{}");
    } catch (error) {
      console.error("Error extracting expense details:", error);
      throw error;
    }
  });
}
